/**
 * useWorkshops Hook - Data fetching and state management for Workshop Builder
 * 
 * This hook handles all API calls and state for the workshop builder feature.
 * Following the architectural pattern used in admin tabs.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export const useWorkshops = (user) => {
  const [workshops, setWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [prerequisiteOptions, setPrerequisiteOptions] = useState({ workshops: [], modules: [] });
  const [loading, setLoading] = useState(true);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // ===== FETCH OPERATIONS =====
  
  const fetchWorkshops = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/my-workshops`, { headers });
      setWorkshops(response.data);
    } catch (error) {
      toast.error('Failed to load workshops');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  }, []);
  
  const fetchTags = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/tags`, { headers });
      setAvailableTags(response.data);
    } catch (error) {
      console.error('Failed to load tags', error);
    }
  }, []);
  
  const fetchPractitioners = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contributors/list`, { headers });
      setPractitioners(response.data.filter(p => p.user?.id !== user.id));
    } catch (error) {
      console.error('Failed to load practitioners', error);
    }
  }, [user.id]);
  
  const fetchPrerequisiteOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workshops/prerequisites/options`, { headers });
      setPrerequisiteOptions(response.data);
    } catch (error) {
      console.error('Failed to load prerequisite options', error);
    }
  }, []);
  
  const fetchWorkshopDetails = useCallback(async (workshopId) => {
    try {
      const response = await axios.get(`${API}/workshops/${workshopId}`, { headers });
      setSelectedWorkshop(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to load workshop details');
      return null;
    }
  }, []);
  
  // ===== WORKSHOP CRUD =====
  
  const createWorkshop = useCallback(async (workshopData) => {
    try {
      await axios.post(`${API}/workshops/`, workshopData, { headers });
      toast.success('Workshop created');
      fetchWorkshops();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workshop');
      return false;
    }
  }, [fetchWorkshops]);
  
  const updateWorkshop = useCallback(async (workshopId, workshopData) => {
    try {
      await axios.put(`${API}/workshops/${workshopId}`, workshopData, { headers });
      toast.success('Workshop updated');
      fetchWorkshops();
      if (selectedWorkshop?.id === workshopId) {
        fetchWorkshopDetails(workshopId);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workshop');
      return false;
    }
  }, [fetchWorkshops, fetchWorkshopDetails, selectedWorkshop]);
  
  const deleteWorkshop = useCallback(async (workshopId) => {
    try {
      await axios.delete(`${API}/workshops/${workshopId}`, { headers });
      toast.success('Workshop deleted');
      fetchWorkshops();
      if (selectedWorkshop?.id === workshopId) {
        setSelectedWorkshop(null);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workshop');
      return false;
    }
  }, [fetchWorkshops, selectedWorkshop]);
  
  const submitForApproval = useCallback(async (workshopId) => {
    try {
      await axios.post(`${API}/workshops/${workshopId}/submit`, {}, { headers });
      toast.success('Workshop submitted for admin approval');
      fetchWorkshops();
      if (selectedWorkshop?.id === workshopId) {
        fetchWorkshopDetails(workshopId);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit workshop');
      return false;
    }
  }, [fetchWorkshops, fetchWorkshopDetails, selectedWorkshop]);
  
  // ===== COLLABORATOR OPERATIONS =====
  
  const inviteCollaborator = useCallback(async (workshopId, data) => {
    try {
      await axios.post(`${API}/workshops/${workshopId}/collaborators`, data, { headers });
      toast.success('Collaborator invited');
      fetchWorkshopDetails(workshopId);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite collaborator');
      return false;
    }
  }, [fetchWorkshopDetails]);
  
  const respondToCollaboration = useCallback(async (workshopId, status, counterPercentage = null) => {
    try {
      await axios.post(`${API}/workshops/${workshopId}/collaborators/respond`, {
        status,
        counter_percentage: counterPercentage
      }, { headers });
      toast.success(`Collaboration ${status}`);
      fetchWorkshops();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
      return false;
    }
  }, [fetchWorkshops]);
  
  const approveFinal = useCallback(async (workshopId) => {
    try {
      await axios.post(`${API}/workshops/${workshopId}/approve-final`, {}, { headers });
      toast.success('Workshop approved');
      fetchWorkshops();
      if (selectedWorkshop?.id === workshopId) {
        fetchWorkshopDetails(workshopId);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
      return false;
    }
  }, [fetchWorkshops, fetchWorkshopDetails, selectedWorkshop]);
  
  // ===== MODULE OPERATIONS =====
  
  const createModule = useCallback(async (workshopId, moduleData) => {
    try {
      await axios.post(`${API}/workshops/${workshopId}/modules`, moduleData, { headers });
      toast.success('Module created');
      fetchWorkshopDetails(workshopId);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create module');
      return false;
    }
  }, [fetchWorkshopDetails]);
  
  const updateModule = useCallback(async (moduleId, moduleData) => {
    try {
      await axios.put(`${API}/workshops/modules/${moduleId}`, moduleData, { headers });
      toast.success('Module updated');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update module');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  const deleteModule = useCallback(async (moduleId) => {
    try {
      await axios.delete(`${API}/workshops/modules/${moduleId}`, { headers });
      toast.success('Module deleted');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete module');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  // ===== FOLDER OPERATIONS =====
  
  const createFolder = useCallback(async (moduleId, folderData) => {
    try {
      await axios.post(`${API}/workshops/modules/${moduleId}/folders`, folderData, { headers });
      toast.success('Folder created');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  const updateFolder = useCallback(async (folderId, folderData) => {
    try {
      await axios.put(`${API}/workshops/folders/${folderId}`, folderData, { headers });
      toast.success('Folder updated');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update folder');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  const deleteFolder = useCallback(async (folderId) => {
    try {
      await axios.delete(`${API}/workshops/folders/${folderId}`, { headers });
      toast.success('Folder deleted');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  // ===== CONTENT OPERATIONS =====
  
  const createContent = useCallback(async (targetId, contentData, isModule = false) => {
    try {
      // Determine endpoint based on whether targeting module or folder
      const endpoint = isModule 
        ? `${API}/workshops/modules/${targetId}/content`
        : `${API}/workshops/folders/${targetId}/content`;
      
      await axios.post(endpoint, contentData, { headers });
      toast.success('Content created');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create content');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  const updateContent = useCallback(async (contentId, contentData) => {
    try {
      await axios.put(`${API}/workshops/content/${contentId}`, contentData, { headers });
      toast.success('Content updated');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update content');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  const deleteContent = useCallback(async (contentId) => {
    try {
      await axios.delete(`${API}/workshops/content/${contentId}`, { headers });
      toast.success('Content deleted');
      if (selectedWorkshop) {
        fetchWorkshopDetails(selectedWorkshop.id);
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete content');
      return false;
    }
  }, [fetchWorkshopDetails, selectedWorkshop]);
  
  // ===== FILE UPLOAD OPERATIONS =====
  
  const uploadCoverImage = useCallback(async (workshopId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      toast.info(`Uploading cover image...`);
      const response = await axios.post(
        `${API}/workshops/${workshopId}/upload-cover`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Cover image uploaded');
      if (selectedWorkshop?.id === workshopId) {
        fetchWorkshopDetails(workshopId);
      }
      fetchWorkshops();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload cover image');
      return null;
    }
  }, [fetchWorkshopDetails, fetchWorkshops, selectedWorkshop, headers]);
  
  const uploadContentFile = useCallback(async (workshopId, file, contentType) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Determine upload endpoint based on content type
    const endpoints = {
      video: 'upload-video',
      audio: 'upload-audio',
      document: 'upload-document',
      pdf: 'upload-document',
      image: 'upload-image'
    };
    
    const endpoint = endpoints[contentType] || 'upload-document';
    
    try {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.info(`Uploading ${file.name} (${sizeMB}MB)...`);
      
      const response = await axios.post(
        `${API}/workshops/${workshopId}/${endpoint}`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('File uploaded successfully');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      return null;
    }
  }, [headers]);
  
  // ===== CATEGORY OPERATIONS (Practitioner-driven) =====
  
  const createCategory = useCallback(async (name) => {
    try {
      const response = await axios.post(
        `${API}/workshops/categories/simple`,
        { name },
        { headers }
      );
      toast.success(`Category "${name}" created`);
      fetchCategories();
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
      return null;
    }
  }, [fetchCategories, headers]);
  
  // ===== WORKSHOP STATUS OPERATIONS =====
  
  const deactivateWorkshop = useCallback(async (workshopId) => {
    try {
      await axios.put(`${API}/workshops/${workshopId}/deactivate`, {}, { headers });
      toast.success('Workshop deactivated - existing purchasers retain access');
      if (selectedWorkshop?.id === workshopId) {
        setSelectedWorkshop(prev => prev ? { ...prev, status: 'inactive' } : null);
      }
      fetchWorkshops();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate workshop');
      return false;
    }
  }, [fetchWorkshops, selectedWorkshop]);
  
  const reactivateWorkshop = useCallback(async (workshopId) => {
    try {
      await axios.put(`${API}/workshops/${workshopId}/reactivate`, {}, { headers });
      toast.success('Workshop reactivated and available for sale');
      if (selectedWorkshop?.id === workshopId) {
        setSelectedWorkshop(prev => prev ? { ...prev, status: 'approved' } : null);
      }
      fetchWorkshops();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate workshop');
      return false;
    }
  }, [fetchWorkshops, selectedWorkshop]);
  
  // ===== UTILITY FUNCTIONS =====
  
  // Fee model (June 2026): buyer pays price + 3% + $0.30; platform's 11% comes
  // out of the price, so the creator nets 89%.
  const calculatePricing = useCallback((price) => {
    const numPrice = parseFloat(price) || 0;
    const platformFee = numPrice * 0.11;
    const processingFee = numPrice * 0.03 + 0.30;
    return {
      platformFee,
      processingFee,
      youReceive: numPrice - platformFee,
      total: numPrice + processingFee,
    };
  }, []);
  
  const getPendingInvitations = useCallback(() => {
    return workshops.filter(w => 
      w.collaborators?.some(c => c.user_id === user.id && c.status === 'pending')
    );
  }, [workshops, user.id]);
  
  return {
    // State
    workshops,
    categories,
    practitioners,
    availableTags,
    prerequisiteOptions,
    loading,
    selectedWorkshop,
    setSelectedWorkshop,
    
    // Fetch operations
    fetchWorkshops,
    fetchCategories,
    fetchPractitioners,
    fetchTags,
    fetchPrerequisiteOptions,
    fetchWorkshopDetails,
    
    // Workshop CRUD
    createWorkshop,
    updateWorkshop,
    deleteWorkshop,
    submitForApproval,
    deactivateWorkshop,
    reactivateWorkshop,
    
    // Collaborator operations
    inviteCollaborator,
    respondToCollaboration,
    approveFinal,
    
    // Module operations
    createModule,
    updateModule,
    deleteModule,
    
    // Folder operations
    createFolder,
    updateFolder,
    deleteFolder,
    
    // Content operations
    createContent,
    updateContent,
    deleteContent,
    
    // Category operations
    createCategory,
    
    // Upload operations
    uploadCoverImage,
    uploadContentFile,
    
    // Utilities
    calculatePricing,
    getPendingInvitations
  };
};
